/**
 * Forbidden-claims linter for Improvement Kit templates and drafts.
 * Blocks unsupported marketing claims unless the owner explicitly provided matching facts.
 */

export type ClaimLintContext = {
  /** Owner confirmed 24/7 / emergency availability text. */
  emergencyAvailability?: string;
  /** Owner-provided years (allows "X years" only when matching). */
  yearsInBusiness?: number | null;
  /** Allow phrases the owner typed into differentiators / warranty. */
  ownerProvidedPhrases?: string[];
};

const FORBIDDEN: Array<{ pattern: RegExp; label: string; unless?: (ctx: ClaimLintContext) => boolean }> = [
  { pattern: /\baward[-\s]?winning\b/i, label: 'award-winning' },
  { pattern: /\b#\s*1\b/i, label: '#1' },
  { pattern: /\bnumber\s*one\b/i, label: 'number one' },
  { pattern: /\bbest\s+(?:in\s+(?:town|city|state)|contractor|plumber|hvac|electrician)\b/i, label: 'best (superlative)' },
  { pattern: /\bguaranteed?\b/i, label: 'guaranteed' },
  {
    pattern: /\b24\s*\/\s*7\b/i,
    label: '24/7',
    unless: (ctx) => Boolean(ctx.emergencyAvailability?.trim()),
  },
  {
    pattern: /\baround\s+the\s+clock\b/i,
    label: 'around the clock',
    unless: (ctx) => Boolean(ctx.emergencyAvailability?.trim()),
  },
  { pattern: /\blowest\s+price\b/i, label: 'lowest price' },
  { pattern: /\bunbeatable\b/i, label: 'unbeatable' },
  { pattern: /\bworld[-\s]?class\b/i, label: 'world-class' },
];

export type ClaimLintIssue = {
  label: string;
  match: string;
};

export function lintClaims(text: string, ctx: ClaimLintContext = {}): ClaimLintIssue[] {
  const issues: ClaimLintIssue[] = [];
  const ownerLower = (ctx.ownerProvidedPhrases ?? []).map((p) => p.toLowerCase());

  for (const rule of FORBIDDEN) {
    if (rule.unless?.(ctx)) continue;
    const re = new RegExp(rule.pattern.source, rule.pattern.flags.includes('g') ? rule.pattern.flags : `${rule.pattern.flags}g`);
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const match = m[0];
      // Allow if the owner literally typed this phrase into a fact field.
      if (ownerLower.some((p) => p.includes(match.toLowerCase()))) continue;
      issues.push({ label: rule.label, match });
    }
  }
  return issues;
}

export function assertNoForbiddenClaims(text: string, ctx?: ClaimLintContext): void {
  const issues = lintClaims(text, ctx);
  if (issues.length) {
    throw new Error(`Forbidden claims: ${issues.map((i) => i.label).join(', ')}`);
  }
}

/** Lint all trade-pack seed strings at boot / in tests. */
export function lintContentBundle(parts: string[], ctx: ClaimLintContext = {}): ClaimLintIssue[] {
  return lintClaims(parts.join('\n'), ctx);
}
